import { useNavigate } from "react-router-dom";
import {
  Ambulance, BriefcaseMedical, Hospital, Watch, Bot, Baby, FlaskConical, Video,
  Stethoscope, Activity, Brain, BookOpen, Info, MessageCircle, Phone, Clock, ShieldCheck,
  MapPin, CheckCircle2, HeartPulse, Home as HomeIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";

// Central config for all service / informational screens (mirrors the mobile screens).
export const SERVICE_CONFIG = {
  ambulance: {
    title: "Ambulance Services", subtitle: "Emergency transport, 24/7", icon: Ambulance, color: "#DC2626",
    hero: { headline: "Life-saving transport, minutes away", text: "Book a BLS or ALS ambulance with trained crew. Live tracking and hospital coordination included." },
    features: [
      { icon: Clock, title: "24/7 Availability", desc: "Round-the-clock dispatch across your city." },
      { icon: MapPin, title: "Live Tracking", desc: "Track the ambulance and share your live location." },
      { icon: HeartPulse, title: "Trained Crew", desc: "BLS/ALS-equipped with paramedic support." },
      { icon: Hospital, title: "Hospital Coordination", desc: "We alert the nearest capable hospital." },
    ],
    ctas: [{ label: "Request Ambulance", to: "/emergency", primary: true }, { label: "Emergency Contacts", to: "/emergency-contacts" }],
  },
  paramedic: {
    title: "On-Demand Paramedic", subtitle: "Medical help at home, fast", icon: BriefcaseMedical, color: "#EA580C",
    hero: { headline: "A paramedic at your doorstep", text: "First-aid, wound care, injections, vitals check and emergency stabilisation at home." },
    features: [
      { icon: Clock, title: "Fast Response", desc: "Nearby paramedics dispatched quickly." },
      { icon: HeartPulse, title: "On-site Care", desc: "Vitals, first-aid, injections & dressing." },
      { icon: ShieldCheck, title: "Verified Professionals", desc: "Background-checked, certified paramedics." },
    ],
    ctas: [{ label: "Request Paramedic", to: "/emergency", primary: true }],
  },
  "health-center": {
    title: "Health Center", subtitle: "Your complete care hub", icon: Hospital, color: "#059669",
    hero: { headline: "Everything for your health, one place", text: "Find hospitals & doctors, book services, and manage your care journey." },
    features: [
      { icon: Hospital, title: "Find Hospitals", desc: "Verified hospitals near you." },
      { icon: Stethoscope, title: "Book Doctors", desc: "Consult top specialists." },
      { icon: FlaskConical, title: "Lab Tests", desc: "Diagnostics at home or in-clinic." },
    ],
    ctas: [{ label: "Explore Care Hub", to: "/care-hub", primary: true }, { label: "Find Hospitals", to: "/find-care" }],
  },
  smartwatch: {
    title: "Smartwatch Health", subtitle: "Wearable insights", icon: Watch, color: "#0EA5E9",
    hero: { headline: "Connect your wearable", text: "Sync heart rate, steps, sleep and SpO₂ to get a fuller picture of your health." },
    features: [
      { icon: HeartPulse, title: "Heart & SpO₂", desc: "Continuous vitals monitoring." },
      { icon: Activity, title: "Activity & Sleep", desc: "Steps, workouts and sleep quality." },
      { icon: ShieldCheck, title: "Private & Secure", desc: "Your data stays encrypted (DPDP)." },
    ],
    ctas: [{ label: "View Daily Tracker", to: "/daily-tracker", primary: true }],
  },
  "health-companion": {
    title: "AI Companion", subtitle: "Your everyday health buddy", icon: Bot, color: "#7C3AED",
    hero: { headline: "Guidance, reminders & answers", text: "Dr Sanjeevani helps with symptoms, medicines, reports and daily nudges — anytime." },
    features: [
      { icon: MessageCircle, title: "Ask Anything", desc: "Chat about symptoms & reports." },
      { icon: Clock, title: "Smart Reminders", desc: "Meds, tests and follow-ups." },
      { icon: HeartPulse, title: "Personalised", desc: "Tailored to your health profile." },
    ],
    ctas: [{ label: "Open Dr Sanjeevani", to: "/assistant", primary: true }],
  },
  "mom-baby": {
    title: "Mom & Baby CareHub", subtitle: "Pregnancy to parenting", icon: Baby, color: "#E91E63",
    hero: { headline: "Care for every milestone", text: "Track your pregnancy journey, vaccinations, growth and get expert guidance." },
    features: [
      { icon: HeartPulse, title: "Pregnancy Journey", desc: "Week-by-week guidance." },
      { icon: ShieldCheck, title: "Vaccinations", desc: "Never miss a shot." },
      { icon: Activity, title: "Growth Tracking", desc: "Milestones for your baby." },
    ],
    ctas: [{ label: "Talk to Dr Sanjeevani", to: "/assistant", primary: true }],
  },
  "at-home-care/lab-tests": {
    title: "Lab Tests at Home", subtitle: "Sample collection at your door", icon: FlaskConical, color: "#16A34A",
    hero: { headline: "Diagnostics without leaving home", text: "Book blood tests, full-body checks and more. Certified phlebotomists, digital reports." },
    features: [
      { icon: HomeIcon, title: "Home Collection", desc: "Trained phlebotomist visits you." },
      { icon: ShieldCheck, title: "Accredited Labs", desc: "NABL-accredited partners." },
      { icon: Clock, title: "Fast Reports", desc: "Digital reports on the app." },
    ],
    ctas: [{ label: "Book a Lab Test", to: "/find-labs", primary: true }],
  },
  "at-home-care/teleconsultation": {
    title: "Teleconsultation", subtitle: "Doctors on video", icon: Video, color: "#2563EB",
    hero: { headline: "Consult a doctor by video", text: "Speak to verified doctors from home. Prescriptions and follow-ups included." },
    features: [
      { icon: Video, title: "Video Consult", desc: "Secure, HD video visits." },
      { icon: Stethoscope, title: "Verified Doctors", desc: "Across specialities." },
      { icon: Clock, title: "Quick Slots", desc: "Same-day appointments." },
    ],
    ctas: [{ label: "Book a Doctor", to: "/doctors", primary: true }],
  },
  "at-home-care/nursing": {
    title: "Nursing at Home", subtitle: "Professional care at home", icon: Stethoscope, color: "#0891B2",
    hero: { headline: "Skilled nurses at home", text: "Post-surgery care, elderly care, injections, wound dressing and monitoring." },
    features: [
      { icon: HeartPulse, title: "Skilled Nurses", desc: "Trained & verified." },
      { icon: Clock, title: "Flexible Shifts", desc: "Hourly, daily or long-term." },
      { icon: ShieldCheck, title: "Safe & Reliable", desc: "Background-checked staff." },
    ],
    ctas: [{ label: "Enquire via Support", to: "/support", primary: true }],
  },
  "at-home-care/physiotherapy": {
    title: "Physiotherapy", subtitle: "Recover at home", icon: Activity, color: "#F59E0B",
    hero: { headline: "Physio sessions at home", text: "Recover from injury, surgery or chronic pain with certified physiotherapists." },
    features: [
      { icon: Activity, title: "Custom Plans", desc: "Tailored rehab programs." },
      { icon: HeartPulse, title: "Pain Management", desc: "Evidence-based therapy." },
      { icon: HomeIcon, title: "At Home", desc: "No commute needed." },
    ],
    ctas: [{ label: "Enquire via Support", to: "/support", primary: true }],
  },
  "at-home-care/advanced-care": {
    title: "Advanced Care", subtitle: "Hospital-grade care at home", icon: Hospital, color: "#DC2626",
    hero: { headline: "ICU-level care at home", text: "Advanced setups for critical & post-hospitalisation care with monitoring." },
    features: [
      { icon: HeartPulse, title: "Monitoring", desc: "Vitals & equipment setup." },
      { icon: Stethoscope, title: "Expert Team", desc: "Doctors & critical-care nurses." },
      { icon: ShieldCheck, title: "24/7 Support", desc: "Round-the-clock care." },
    ],
    ctas: [{ label: "Enquire via Support", to: "/support", primary: true }],
  },
  "at-home-care/therapy": {
    title: "Therapy Sessions", subtitle: "Mental wellness support", icon: Brain, color: "#7C3AED",
    hero: { headline: "Talk to a therapist", text: "Confidential counselling and therapy for stress, anxiety and more." },
    features: [
      { icon: Brain, title: "Licensed Therapists", desc: "Confidential & compassionate." },
      { icon: Video, title: "Online or At Home", desc: "Your choice of mode." },
      { icon: ShieldCheck, title: "Private", desc: "Encrypted & secure (DPDP)." },
    ],
    ctas: [{ label: "Talk to Dr Sanjeevani", to: "/assistant", primary: true }],
  },
  support: {
    title: "Help & Support", subtitle: "We're here to help", icon: MessageCircle, color: "#059669",
    hero: { headline: "Need a hand?", text: "Reach our support team for bookings, reports, payments or any app help." },
    features: [
      { icon: Phone, title: "Call Us", desc: "Speak to a support agent." },
      { icon: MessageCircle, title: "Chat", desc: "Ask Dr Sanjeevani for instant help." },
      { icon: BookOpen, title: "App Guide", desc: "Learn how features work." },
    ],
    ctas: [{ label: "Ask Dr Sanjeevani", to: "/assistant", primary: true }, { label: "Open App Guide", to: "/app-guide" }],
  },
  "app-guide": {
    title: "App Guide", subtitle: "How to use Trata Health", icon: BookOpen, color: "#2563EB",
    hero: { headline: "Get the most out of Trata Health", text: "A quick tour of the key features and how to use them." },
    features: [
      { icon: Ambulance, title: "Emergency SOS", desc: "One tap to call for help." },
      { icon: Stethoscope, title: "Book Care", desc: "Doctors, hospitals & labs." },
      { icon: Bot, title: "Dr Sanjeevani", desc: "Your AI health assistant." },
      { icon: FlaskConical, title: "Health Records", desc: "Store & analyse reports." },
    ],
    ctas: [{ label: "See How It Works", to: "/how-it-works", primary: true }],
  },
  "how-it-works": {
    title: "How It Works", subtitle: "Trata Health in 3 steps", icon: Info, color: "#7C3AED",
    hero: { headline: "The Missing Link in Indian Healthcare", text: "From emergencies to everyday care, Trata connects you to the right help fast." },
    steps: [
      { n: 1, title: "Tell us what you need", desc: "Emergency, a doctor, a lab test or health advice." },
      { n: 2, title: "We connect you", desc: "Verified hospitals, doctors, ambulances and labs near you." },
      { n: 3, title: "Care & track", desc: "Book, track live, store reports and follow up — all in one app." },
    ],
    features: [
      { icon: ShieldCheck, title: "DPDP Compliant", desc: "Your data is encrypted & private." },
      { icon: Hospital, title: "Verified Network", desc: "Trusted partners only." },
    ],
    ctas: [{ label: "Get Started", to: "/home", primary: true }],
  },
};

export default function ServicePage({ slug }) {
  const navigate = useNavigate();
  const cfg = SERVICE_CONFIG[slug];
  if (!cfg) return <div className="p-6">Not found</div>;
  const Icon = cfg.icon;

  return (
    <div className="max-w-3xl" data-testid={`service-page-${slug.replace(/\//g, "-")}`}>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} icon={Icon} color={cfg.color} />

      {/* Hero */}
      <Card className="rounded-3xl p-6 border-border" style={{ backgroundColor: cfg.color + "0D" }}>
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: cfg.color + "1A" }}>
            <Icon className="h-6 w-6" style={{ color: cfg.color }} />
          </div>
          <div>
            <h2 className="text-xl font-semibold">{cfg.hero.headline}</h2>
            <p className="text-muted-foreground mt-1.5">{cfg.hero.text}</p>
          </div>
        </div>
      </Card>

      {/* Steps */}
      {cfg.steps && (
        <div className="mt-6 space-y-3">
          {cfg.steps.map((s) => (
            <div key={s.n} className="flex items-start gap-4 bg-card rounded-2xl border border-border p-4">
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: cfg.color }}>
                {s.n}
              </div>
              <div>
                <p className="font-semibold">{s.title}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Features */}
      {cfg.features && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cfg.features.map((f) => {
            const FIcon = f.icon;
            return (
              <div key={f.title} className="flex items-start gap-3 bg-card rounded-2xl border border-border p-4">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: cfg.color + "14" }}>
                  <FIcon className="h-5 w-5" style={{ color: cfg.color }} />
                </div>
                <div>
                  <p className="font-semibold text-sm">{f.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CTAs */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        {cfg.ctas.map((c) => (
          <Button
            key={c.label}
            onClick={() => navigate(c.to)}
            data-testid={`service-cta-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            variant={c.primary ? "default" : "outline"}
            className="h-12 rounded-2xl text-base flex-1"
            style={c.primary ? { backgroundColor: cfg.color } : {}}
          >
            {c.primary && <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            {c.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
